/**
 * Edit History dialog for Shipment Costing.
 *
 * - Lists rows from `/api/shipment-costing/audit-history`.
 * - Shows who/when/what changed (before → after).
 * - Revert button per row → prompts admin password → fires the revert
 *   endpoint, then refreshes the list.
 *
 * Props
 * -----
 * open, onClose       dialog plumbing
 * token               auth bearer
 * onReverted          parent callback to refresh the table after a revert
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Undo2, RefreshCw, Search, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const fmtTs = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso || "—"; }
};

export const EditHistoryDialog = ({ open, onClose, token, onReverted }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingRevert, setPendingRevert] = useState(null);
  const [revertPwd, setRevertPwd] = useState("");
  const [reverting, setReverting] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/audit-history?limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setItems(d.items || []);
    } catch (err) {
      toast.error(`Failed to load history: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchHistory();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (it.tracking || "").toLowerCase().includes(q) ||
      (it.user || "").toLowerCase().includes(q) ||
      JSON.stringify(it.after || {}).toLowerCase().includes(q) ||
      JSON.stringify(it.before || {}).toLowerCase().includes(q)
    );
  });

  const doRevert = async () => {
    if (!pendingRevert || !revertPwd) return;
    setReverting(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/shipment-costing/audit-history/${pendingRevert.audit_id}/revert`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ admin_password: revertPwd }),
        },
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || `HTTP ${res.status}`);
      toast.success("Reverted");
      if (d.affected_tracking && d.needs_recalc) {
        fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-by-tracking`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ tracking_numbers: [d.affected_tracking] }),
        }).catch(() => {});
      }
      setPendingRevert(null);
      setRevertPwd("");
      fetchHistory();
      onReverted?.();
    } catch (err) {
      toast.error(`Revert failed: ${err.message}`);
    } finally {
      setReverting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="max-w-5xl max-h-[88vh] flex flex-col" data-testid="edit-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <HistoryIcon className="w-5 h-5" />
              Shipment Costing — Edit History
            </DialogTitle>
            <DialogDescription>
              Every inline-edit on this table is logged here. Click "Revert" to roll back an entry (requires admin password).
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by tracking / user / field value…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-md"
                data-testid="history-search"
              />
            </div>
            <Button variant="outline" size="sm" onClick={fetchHistory} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          <div className="flex-1 overflow-auto border rounded-lg">
            {loading ? (
              <div className="p-8 text-center text-slate-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                <div className="text-xs mt-2">Loading history…</div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No edits found.</div>
            ) : (
              <table className="w-full text-xs" data-testid="history-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left">Time</th>
                    <th className="px-2 py-2 text-left">User</th>
                    <th className="px-2 py-2 text-left">Tracking</th>
                    <th className="px-2 py-2 text-left">Changes</th>
                    <th className="px-2 py-2 text-center">Status</th>
                    <th className="px-2 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => {
                    const fields = Object.keys(it.after || {});
                    return (
                      <tr key={it.audit_id} className="border-t hover:bg-indigo-50/30">
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtTs(it.timestamp)}</td>
                        <td className="px-2 py-1.5">{it.user || "—"}</td>
                        <td className="px-2 py-1.5 font-mono">{it.tracking || "—"}</td>
                        <td className="px-2 py-1.5">
                          {fields.map((f) => (
                            <div key={f} className="text-[11px]">
                              <span className="font-semibold text-slate-700">{f}:</span>{" "}
                              <span className="text-rose-600 line-through">{fmtVal(it.before?.[f])}</span>{" "}
                              →{" "}
                              <span className="text-emerald-700 font-semibold">{fmtVal(it.after?.[f])}</span>
                            </div>
                          ))}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {it.reverted ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Reverted</span>
                          ) : it.revert_of ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">↺ Revert</span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Applied</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {!it.reverted && !it.revert_of && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setPendingRevert(it); setRevertPwd(""); }}
                              className="h-6 px-2 text-[10.5px] text-amber-700 hover:bg-amber-50"
                              data-testid={`revert-btn-${it.audit_id}`}
                            >
                              <Undo2 className="w-3 h-3 mr-0.5" /> Revert
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose} data-testid="history-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert confirmation password modal */}
      <Dialog open={!!pendingRevert} onOpenChange={(v) => !v && setPendingRevert(null)}>
        <DialogContent className="max-w-md" data-testid="revert-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700">Confirm Revert</DialogTitle>
            <DialogDescription>
              This will roll back the change shown below. Enter admin password to confirm.
            </DialogDescription>
          </DialogHeader>
          {pendingRevert && (
            <div className="text-xs space-y-1 bg-amber-50 border border-amber-200 rounded p-2">
              <div><span className="font-semibold">Tracking:</span> {pendingRevert.tracking || "—"}</div>
              <div><span className="font-semibold">When:</span> {fmtTs(pendingRevert.timestamp)}</div>
              <div><span className="font-semibold">By:</span> {pendingRevert.user || "—"}</div>
              <div className="pt-1">
                {Object.keys(pendingRevert.after || {}).map((f) => (
                  <div key={f}>
                    <b>{f}:</b>{" "}
                    <span className="text-emerald-700">{fmtVal(pendingRevert.after?.[f])}</span> →{" "}
                    <span className="text-rose-600">{fmtVal(pendingRevert.before?.[f])}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <input
            type="password"
            name="revert-admin-pwd-new"
            autoComplete="new-password"
            data-lpignore="true"
            data-form-type="other"
            placeholder="Admin password"
            value={revertPwd}
            onChange={(e) => setRevertPwd(e.target.value)}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") doRevert(); }}
            className="w-full px-3 py-2 text-sm border rounded-md"
            autoFocus
            data-testid="revert-pwd-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingRevert(null)}>Cancel</Button>
            <Button onClick={doRevert} disabled={!revertPwd || reverting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="revert-confirm-btn">
              {reverting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}
              Revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditHistoryDialog;
